import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';

import {
  useGetPlans,
  useGetRestrictions,
} from '../../../redux/api/subscriptionsApi';
import {
  RestrictionScope,
  SubscriptionPeriod,
} from '../../../shared/constants';
import { ISubscriptionPlan } from '../../../types/interfaces/subscription-plan';
import { IUserSubscription } from '../../../types/interfaces/userSubscription';
import AppTabsBase from '../../designe/app-tabs/AppTabsBase';
import { useStyles } from './ChoosePlan.styles';
import PlanItem from './plan-item/PlanItem';

interface ChoosePlanPros {
  currentPlan?: IUserSubscription;
  selectedPlan?: ISubscriptionPlan | null;
  onPlanSelected: (plan: ISubscriptionPlan) => void;
  pendingPlanId?: string | null;
  isPlanMutationLoading?: boolean;
}

function ChoosePlan({
  selectedPlan,
  onPlanSelected,
  currentPlan,
  pendingPlanId = null,
  isPlanMutationLoading = false,
}: ChoosePlanPros) {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const { data: plans } = useGetPlans();
  const { data: restrictions } = useGetRestrictions();
  const [selectedPeriod, setSelectedPeriod] = useState(
    SubscriptionPeriod.annual,
  );

  useEffect(() => {
    if (selectedPlan) {
      setSelectedPeriod(selectedPlan.period);
    }
  }, [selectedPlan]);

  function getPeriodPlans() {
    return (
      plans
        ?.filter((plan) => plan.period === selectedPeriod)
        .sort((a, b) => a?.orderNumber - b?.orderNumber) || []
    );
  }

  function getPlanRestrictions(plan: ISubscriptionPlan) {
    if (!restrictions) {
      return [];
    }

    const scopeOrder: Record<RestrictionScope, number> = {
      [RestrictionScope.exercise]: 0,
      [RestrictionScope.program]: 1,
      [RestrictionScope.programItem]: 2,
      [RestrictionScope.training]: 3,
      [RestrictionScope.trainingGeneration]: 4,
      [RestrictionScope.periodStatistics]: 5,
    };

    return restrictions
      .filter(
        (restriction) => restriction.type.toString() === plan.scope.toString(),
      )
      .sort(
        (a, b) =>
          scopeOrder[a.restrictionScope] - scopeOrder[b.restrictionScope],
      );
  }

  function getSavedPercent(plan: ISubscriptionPlan): number | null {
    if (plan.period === SubscriptionPeriod.monthly) {
      return null;
    }

    const monthlyPlan = plans.find(
      (p) => p.period !== plan.period && p.scope === plan.scope,
    );

    if (!monthlyPlan?.price) {
      return null;
    }

    const fullAnnualPrice = monthlyPlan.price * 12;
    const savedAmount = fullAnnualPrice - plan.price;

    return (savedAmount / fullAnnualPrice) * 100;
  }

  const periodTabsConfig = [
    {
      label: t('choosePlan.selectedPeriodAnnual'),
      value: SubscriptionPeriod.annual,
    },
    {
      label: t('choosePlan.selectedPeriodMonthly'),
      value: SubscriptionPeriod.monthly,
    },
  ];

  return (
    <div className={classes.choosePlanWrapper}>
      <div className={classes.periodButtonWrapper}>
        <AppTabsBase
          config={periodTabsConfig}
          value={selectedPeriod}
          onChange={(value) => setSelectedPeriod(value as SubscriptionPeriod)}
          options={{ fitContent: true }}
        />
      </div>
      <div className={classes.plansWrapper}>
        {getPeriodPlans().map((plan: ISubscriptionPlan) => (
          <PlanItem
            key={plan.id}
            plan={plan}
            currentPlan={currentPlan}
            restrictions={getPlanRestrictions(plan)}
            savePercent={getSavedPercent(plan)}
            onClick={onPlanSelected}
            loading={
              Boolean(pendingPlanId) &&
              pendingPlanId === plan.id &&
              isPlanMutationLoading
            }
          />
        ))}
      </div>
    </div>
  );
}

export default ChoosePlan;
